import CheckCircleOutlined from '@ant-design/icons/lib/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/lib/icons/CloseCircleOutlined';
import App from 'antd/lib/app';
import Layout from 'antd/lib/layout';
import Table from 'antd/lib/table';
import { ColumnsType } from 'antd/lib/table';
import { parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useDocumentTitle } from 'src/components/useDocumentTitle';
import { PageHeader } from 'src/components/PageHeader';
import { PjmBreadcrumbNav } from '../PjmBreadcrumbNav';
import { PjmPollingStatusItemDto, PjmPollingRequestType, usePjmPollingStatusQuery } from 'src/domain/pjm/queries';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { ERROR_COLOR, PRIMARY_COLOR } from 'src/theme';
import { safeParseJSON } from 'src/domain/safeParseJSON';
import CalendarTwoTone from '@ant-design/icons/lib/icons/CalendarTwoTone';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import Space from 'antd/lib/space';
import { Tooltip } from 'antd/lib';

const REQUEST_TYPE_LABELS: Record<PjmPollingRequestType, string> = {
    'load-management-events': 'Load Management Events',
    'load-management-events:test': 'Load Management Events (Test)',
};

const REQUEST_TYPES = Object.keys(REQUEST_TYPE_LABELS) as PjmPollingRequestType[];

interface TableRow {
    key: number;
    records: Partial<Record<PjmPollingRequestType, PjmPollingStatusItemDto>>;
}

function buildRows(data: PjmPollingStatusItemDto[]): TableRow[] {
    const sorted = [...data].sort((a, b) => parseISO(b.datetime).getTime() - parseISO(a.datetime).getTime());
    const byType: Partial<Record<PjmPollingRequestType, PjmPollingStatusItemDto[]>> = {};

    for (const item of sorted) {
        (byType[item.type] ??= []).push(item);
    }

    const maxLen = Math.max(0, ...Object.values(byType).map(arr => arr.length));

    return Array.from({ length: maxLen }, (_, i) => {
        const records = Object.fromEntries(REQUEST_TYPES.map(type => [type, byType[type]?.[i]])) as Partial<
            Record<PjmPollingRequestType, PjmPollingStatusItemDto>
        >;
        return { key: i, records };
    });
}

function ResultCell({ item }: { item: PjmPollingStatusItemDto | undefined }) {
    if (!item) return <span style={{ color: '#bbb' }}>—</span>;

    if (item.success) {
        const resultParsed = safeParseJSON(item.result);
        const receivedEvents = resultParsed?.events?.length > 0;
        const tooltip = receivedEvents ? 'Successful request to PJM and received events' : 'Successful request to PJM but no events received';

        return (
            <Tooltip title={tooltip}>
                <Space>
                    <CheckCircleOutlined style={{ color: PRIMARY_COLOR }} />
                    {getComputerFormattedTime(item.datetime)}
                    {receivedEvents ? <CalendarTwoTone style={{ fontSize: '24px' }} /> : null}
                </Space>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={'Failed or missed request to PJM'}>
            <Space>
                <CloseCircleOutlined style={{ color: ERROR_COLOR }} />
                {item.result}
            </Space>
        </Tooltip>
    );
}

export const PjmPollingStatus = () => {
    const { notification } = App.useApp();

    useDocumentTitle('PJM Polling Status');

    const { data, isError, error, isFetching } = usePjmPollingStatusQuery();

    if (isError) {
        notification.error({ key: 'fetch-pjm-polling-status-error', message: error.message || 'Cannot fetch PJM Polling Status!' });
    }

    const rows = useMemo(() => buildRows(data || []), [data]);

    const columns: ColumnsType<TableRow> = REQUEST_TYPES.map(type => ({
        key: type,
        title: REQUEST_TYPE_LABELS[type],
        render: (_: any, row: TableRow) => <ResultCell item={row.records[type]} />,
    }));

    return (
        <Layout style={{ height: '100%' }} className="pjm-polling-status">
            <Layout.Content style={{ padding: '0 26px' }}>
                <PageHeader
                    pageTitle={<PjmBreadcrumbNav />}
                    subTitle={
                        <Space>
                            <InfoCircleOutlined />
                            This page displays most recent 30 minutes of requests to PJM
                        </Space>
                    }
                    actions={[]}
                />
                <Table<TableRow> size="small" rowKey="key" columns={columns} dataSource={rows} loading={isFetching} pagination={false} />
            </Layout.Content>
        </Layout>
    );
};
